import { forwardRef } from 'react';

import { Input } from '@chakra-ui/react';
import { formats } from '@lingui/core';

import { getDateLocale } from './langUtil';
import { isStrNullOrEmpty } from './strUtil';

const MONTH_FIRST_DATE_LOCALES = ['en-US'];

export const formatDate = (date, format) => formats.date(getDateLocale(), date, format);

export const isMonthFirstDateFormat = () => MONTH_FIRST_DATE_LOCALES.includes(getDateLocale());

export const getDatePickerDateFormat = () => (isMonthFirstDateFormat() ? 'MM/dd/yyyy' : 'dd/MM/yyyy');

export const getDatePickerDayMonthFormat = () => (isMonthFirstDateFormat() ? 'MM/dd' : 'dd/MM');

const isTwelveHourClock = () => new Intl.DateTimeFormat(getDateLocale(), { hour: 'numeric' }).resolvedOptions().hour12 === true;

const formatFilterHour = (hour, twelveHourClock) => (twelveHourClock ? formatDate(new Date(2000, 0, 1, hour), { hour: 'numeric' }) : hour + 'h');

export const dateToIsoString = date => {
  if (!date || date === '') return '';

  const offset = date.getTimezoneOffset();
  const newDate = new Date(date.getTime() - offset * 60 * 1000);
  return newDate.toISOString().substring(0, 10);
};

// This by default creates a list from 0h to 23h
// If the startHour is specified, eg: 6, then
// it will create a list from 6h to 23h and from 0h to 5h
export const getFilterHourArray = (startHour = 0, hourStep = 1) => {
  const hourFilter = [{ label: '-', value: '' }];
  const twelveHourClock = isTwelveHourClock();

  const pushHour = hour => {
    return hourFilter.push({ label: formatFilterHour(hour, twelveHourClock), value: hour });
  };

  for (let i = startHour; i < 24; i += hourStep) {
    pushHour(i);
  }
  for (let i = 0; i < startHour; i += hourStep) {
    pushHour(i);
  }

  return hourFilter;
};

export const addDaysToDate = (dateObj, days) => {
  dateObj.setDate(dateObj.getDate() + days);
  return dateObj;
};

export const getThisMonday = () => {
  const date = new Date();
  const day = date.getDay() || 7; // Get current day number, converting Sun. to 7
  // Only manipulate the date if it isn't Mon.
  if (day !== 1) {
    date.setHours(-24 * (day - 1)); // Set the hours to day number minus 1 multiplied by negative 24
  }
  return date;
};

export const getNextMonday = () => addDaysToDate(getThisMonday(), 7);

// This is used to adapt react datepicker with chakra UI
const customDateInput = (props, ref) => <Input borderColor="brand.color_main" ref={ref} {...props} cursor="pointer" />;
export const ChakraDateInput = forwardRef(customDateInput);

// input type text for date : automatic slash management
// Modified from https://stackoverflow.com/a/44138117
export const DATE_SEPARATOR_FRONTEND = ' / '; // We use spaces for the automatic slash management below
const MAX_DATE_LEN = 14; // "DD / MM / YYYY", the spaces are used to delete automatically
export const autoHandleDateSlash = inputDateValue => {
  if (inputDateValue.length > MAX_DATE_LEN) return inputDateValue.substr(0, MAX_DATE_LEN);

  // This is when we delete
  if (/\D\/$/.test(inputDateValue)) {
    inputDateValue = inputDateValue.substr(0, inputDateValue.length - 3);
  }

  const splittedDate = inputDateValue.split('/').map(function (v) {
    return v.replace(/\D/g, '');
  });

  const [firstMaxAllowed, secondMaxAllowed] = isMonthFirstDateFormat() ? [12, 31] : [31, 12];
  if (splittedDate[0]) splittedDate[0] = checkDateSplitElement(splittedDate[0], firstMaxAllowed);
  if (splittedDate[1]) splittedDate[1] = checkDateSplitElement(splittedDate[1], secondMaxAllowed);

  const unSplittedDate = splittedDate.map(function (v, i) {
    return v.length == 2 && i < 2 ? v + DATE_SEPARATOR_FRONTEND : v;
  });
  const formattedDate = unSplittedDate.join('').substr(0, MAX_DATE_LEN);
  return formattedDate;
};

const checkDateSplitElement = (splitDateValue, maxAllowed) => {
  if (splitDateValue.charAt(0) !== '0' || splitDateValue == '00') {
    let num = parseInt(splitDateValue);
    if (isNaN(num) || num <= 0 || num > maxAllowed) num = 1;
    const numStr = num.toString();
    splitDateValue = num > parseInt(maxAllowed.toString().charAt(0)) && numStr.length == 1 ? '0' + num : numStr;
  }
  return splitDateValue;
};

const splitInputDate = inputDate => {
  const [first, second, year] = inputDate.split(DATE_SEPARATOR_FRONTEND).map(part => part.trim());
  return isMonthFirstDateFormat() ? { day: second, month: first, year } : { day: first, month: second, year };
};

const joinInputDate = ({ day, month, year }) => (isMonthFirstDateFormat() ? [month, day, year] : [day, month, year]).join(DATE_SEPARATOR_FRONTEND);

const ISO_DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})/;
// Before the ISO storage, the text input dates were stored as "DD / MM / YYYY" whatever the locale
const LEGACY_INPUT_DATE_REGEX = /^(\d{1,2})\s*\/\s*(\d{1,2})\s*\/\s*(\d{4})$/;

const parseIsoOrLegacyDate = date => {
  const isoMatch = ISO_DATE_REGEX.exec(date);
  if (isoMatch) return { year: isoMatch[1], month: isoMatch[2], day: isoMatch[3] };

  const legacyMatch = LEGACY_INPUT_DATE_REGEX.exec(date.trim());
  if (legacyMatch) return { year: legacyMatch[3], month: legacyMatch[2].padStart(2, '0'), day: legacyMatch[1].padStart(2, '0') };

  return null;
};

// Returns "YYYY-MM-DD" from a stored date (ISO or legacy), null if there is no valid stored date
export const toIsoDate = date => {
  const parsedDate = date ? parseIsoOrLegacyDate(String(date)) : null;
  return parsedDate ? `${parsedDate.year}-${parsedDate.month}-${parsedDate.day}` : null;
};

export const isoDateToInputDate = isoDate => {
  const parsedDate = isoDate ? parseIsoOrLegacyDate(String(isoDate)) : null;
  return parsedDate ? joinInputDate(parsedDate) : '';
};

// Returns an empty string while the text input date is not complete
export const inputDateToIsoDate = inputDate => {
  const { day, month, year } = splitInputDate(inputDate ?? '');
  const isComplete = /^\d{2}$/.test(day) && /^\d{2}$/.test(month) && /^\d{4}$/.test(year);
  return isComplete ? `${year}-${month}-${day}` : '';
};

// Returns null if date is empty, true if valid, false for any other case
export const isValidPastDate = dateStr => {
  if (isStrNullOrEmpty(dateStr)) return null;

  const { day: dd, month: mm, year: yyyy } = splitInputDate(dateStr);
  if (!dd || !mm || !yyyy) return false;

  if (yyyy.length !== 4 || mm.length !== 2 || dd.length !== 2) return false;

  const d = Number(dd);
  const m = Number(mm);
  const y = Number(yyyy);

  if (!Number.isInteger(d) || !Number.isInteger(m) || !Number.isInteger(y)) return false;
  if (y < 1900 || m < 1 || m > 12 || d < 1 || d > 31) return false;

  // local time, not UTC parsing
  const date = new Date(y, m - 1, d);

  // validate (catches 31 FEB etc)
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return false;

  // strictly before today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  return date < today;
};

export const getDaysOfMonthArray = (year, month) => {
  const daysOfMonth = [];
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);
  for (let d = monthStart; d <= monthEnd; d.setDate(d.getDate() + 1)) {
    daysOfMonth.push(new Date(d));
  }
  return daysOfMonth;
};

export const isSameDayOfDates = (dateA, dateB) => dateA && dateB && dateA.toDateString() === dateB.toDateString();
